import React from 'react';
import {
  PREMIUM_PLANTS,
  PREMIUM_PLANT_ORDER,
  RIBBON_BLADES,
  LEAFY_LEAVES,
  type PremiumPlantId,
} from './premiumPlantData';
import { pvDuration, pvDelay, type PvStyle } from '../motionStyles';

interface PremiumPlantProps {
  variant: PremiumPlantId;
  reducedMotion: boolean;
  quality: 'low' | 'balanced' | 'high';
  speed: number;
  active?: boolean;
  dimmed?: boolean;
}

type Origin = 'bottom center' | 'bottom left' | 'bottom right';

function swayStyle(duration: string, delay: string, amp: string, origin: Origin): PvStyle {
  return {
    transformBox: 'fill-box',
    transformOrigin: origin,
    '--pv-duration': duration,
    '--pv-delay': delay,
    '--pv-amp': amp,
  };
}

/**
 * One premium plant. All variants share the same sway model; only the
 * silhouette, palette and per-blade timing differ.
 */
export function PremiumPlant({ variant, reducedMotion, quality, speed, active = false, dimmed = false }: PremiumPlantProps) {
  const def = PREMIUM_PLANTS[variant];
  const p = def.palette;
  const m = def.motion;
  const reduced = reducedMotion;
  const quiet = quality === 'low';

  const rootClass = [
    'pv-asset',
    active ? 'is-active' : '',
    dimmed ? 'is-dim' : '',
    reduced ? 'pv-reduced' : '',
    quiet ? 'pv-quiet' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const uid = `pvplant-${variant}`;

  if (variant === 'ribbon') {
    const blades = quiet ? RIBBON_BLADES.slice(0, 3) : RIBBON_BLADES;
    return (
      <div className={rootClass} style={{ width: '100%' }}>
        <svg viewBox="0 0 140 220" style={{ width: '100%', height: 'auto', display: 'block' }} role="img" aria-label="ThriveCove premium ribbon sea plant">
          <defs>
            <linearGradient id={`${uid}-grad`} x1="0%" y1="100%" x2="0%" y2="0%">
              <stop offset="0%" stopColor={p.primaryDark} />
              <stop offset="55%" stopColor={p.primary} />
              <stop offset="100%" stopColor={p.primaryLight} />
            </linearGradient>
          </defs>
          <ellipse cx={70} cy={212} rx={46} ry={9} fill="#153f42" opacity="0.16" />
          <path d="M 42 212 Q 70 202 98 212 Q 70 219 42 212 Z" fill={p.primaryDark} opacity="0.85" />
          {blades.map((d, i) => (
            <g
              key={i}
              className={`pv-anim pv-sway${i >= 3 ? ' pv-secondary' : ''}`}
              style={swayStyle(pvDuration(m.sway + i * 0.5, speed), pvDelay(m.delay + i * 0.4), m.amp, 'bottom center')}
            >
              <path d={d} fill={`url(#${uid}-grad)`} opacity={i % 2 === 0 ? 0.95 : 0.82} />
            </g>
          ))}
          <path d="M 70 210 C 63 160 58 110 64 62" fill="none" stroke={p.accent} strokeWidth="1.2" opacity="0.35" />
        </svg>
      </div>
    );
  }

  if (variant === 'leafy') {
    const leaves = quiet ? LEAFY_LEAVES.slice(0, 3) : LEAFY_LEAVES;
    const origins: Record<string, Origin> = { left: 'bottom right', right: 'bottom left', center: 'bottom center' };
    return (
      <div className={rootClass} style={{ width: '100%' }}>
        <svg viewBox="0 0 150 190" style={{ width: '100%', height: 'auto', display: 'block' }} role="img" aria-label="ThriveCove premium leafy plant">
          <defs>
            <linearGradient id={`${uid}-leaf`} x1="0%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor={p.primaryLight} />
              <stop offset="55%" stopColor={p.primary} />
              <stop offset="100%" stopColor={p.primaryDark} />
            </linearGradient>
          </defs>
          <ellipse cx={75} cy={184} rx={44} ry={9} fill="#153f42" opacity="0.16" />
          <path d="M 75 186 Q 71 140 74 100 Q 77 60 75 40" fill="none" stroke={p.stem} strokeWidth="9" strokeLinecap="round" />
          <path d="M 75 186 Q 71 140 74 100 Q 77 60 75 40" fill="none" stroke={p.primaryLight} strokeWidth="2" strokeLinecap="round" opacity="0.4" />
          {leaves.map((leaf, i) => (
            <g
              key={i}
              className={`pv-anim pv-leaf${i >= 3 ? ' pv-secondary' : ''}`}
              style={swayStyle(pvDuration(m.sway + i * 0.45, speed), pvDelay(m.delay + i * 0.35), m.amp, origins[leaf.side])}
            >
              <path d={leaf.d} fill={`url(#${uid}-leaf)`} />
              <path d={leaf.d} fill="none" stroke={p.accent} strokeWidth="1" opacity="0.35" />
            </g>
          ))}
          <circle cx={75} cy={40} r={4} fill={p.glow} opacity="0.7" />
        </svg>
      </div>
    );
  }

  // coral
  return (
    <div className={rootClass} style={{ width: '100%' }}>
      <svg viewBox="0 0 150 180" style={{ width: '100%', height: 'auto', display: 'block' }} role="img" aria-label="ThriveCove premium soft coral plant">
        <defs>
          <linearGradient id={`${uid}-branch`} x1="0%" y1="100%" x2="0%" y2="0%">
            <stop offset="0%" stopColor={p.primaryDark} />
            <stop offset="60%" stopColor={p.primary} />
            <stop offset="100%" stopColor={p.primaryLight} />
          </linearGradient>
        </defs>
        <ellipse cx={75} cy={174} rx={46} ry={9} fill="#153f42" opacity="0.16" />
        <g className="pv-anim pv-float" style={swayStyle(pvDuration(m.float, speed), pvDelay(m.delay), m.amp, 'bottom center')}>
          <path d="M 35 170 Q 45 140 75 138 Q 105 140 115 170 Q 95 178 75 178 Q 55 178 35 170 Z" fill={`url(#${uid}-branch)`} opacity="0.95" />
          <g className="pv-anim pv-sway" style={swayStyle(pvDuration(m.sway, speed), pvDelay(m.delay), m.amp, 'bottom center')}>
            <path d="M 60 150 C 52 120 48 96 54 74 C 58 60 66 52 70 58 C 76 66 74 84 70 100 C 66 122 66 138 68 150 Z" fill={`url(#${uid}-branch)`} />
          </g>
          <g className="pv-anim pv-sway" style={swayStyle(pvDuration(m.sway + 0.7, speed), pvDelay(m.delay + 0.5), m.amp, 'bottom center')}>
            <path d="M 78 148 C 80 116 88 92 100 74 C 108 62 116 58 118 66 C 120 76 110 92 102 106 C 92 124 88 138 86 150 Z" fill={`url(#${uid}-branch)`} />
          </g>
          <g className={`pv-anim pv-sway${quiet ? ' pv-secondary' : ''}`} style={swayStyle(pvDuration(m.sway + 1.1, speed), pvDelay(m.delay + 0.9), m.amp, 'bottom center')}>
            <path d="M 70 152 C 68 132 72 116 80 104 C 86 96 92 94 92 100 C 92 108 86 118 82 128 C 78 140 76 146 76 152 Z" fill={`url(#${uid}-branch)`} />
          </g>
          <circle cx={56} cy={70} r={6} fill={p.primaryLight} opacity="0.9" />
          <circle cx={118} cy={66} r={5} fill={p.primaryLight} opacity="0.9" />
          <circle cx={90} cy={98} r={4.5} fill={p.primaryLight} opacity="0.85" />
        </g>
        {!quiet && (
          <g>
            <circle className="pv-anim pv-shimmer pv-secondary" cx={40} cy={110} r={3} fill={p.accent} style={{ '--pv-duration': pvDuration(4, speed), '--pv-delay': pvDelay(0.4) } as PvStyle} />
            <circle className="pv-anim pv-shimmer pv-secondary" cx={112} cy={96} r={2.4} fill={p.accent} style={{ '--pv-duration': pvDuration(5, speed), '--pv-delay': pvDelay(1.2) } as PvStyle} />
          </g>
        )}
      </svg>
    </div>
  );
}

export { PREMIUM_PLANTS, PREMIUM_PLANT_ORDER };
